import React, { createContext, useEffect, useState } from "react";
import { check, getQueryParam } from "@/helpers/utils";
import {
  DroppsProduct,
  DroppsVariant,
  productSelect,
  ScentDetails,
  DroppsSellingPlan,
  variantSelect,
} from "@/store";
import { create, StoreApi, useStore } from "zustand";

interface ProductProviderProps {
  product: DroppsProduct;
  scents: ScentDetails[];
}

interface PdpState {
  product: DroppsProduct;
  focusedProduct: DroppsProduct;
  variant: DroppsVariant;
  scents: ScentDetails[];
  subscription: {
    preference: boolean | undefined;
    sellingPlan: DroppsSellingPlan | undefined;
  };
  actions: {
    focusProduct: (product: DroppsProduct) => void;
    unfocusProduct: () => void;
    selectProduct: (product: DroppsProduct) => void;
    selectVariant: (variant: DroppsVariant) => void;
    setSubscriptionPreference: (prefersSubscription: boolean) => void;
  };
}

const PdpContext = createContext<StoreApi<PdpState> | null>(null);

export const PdpProvider = ({
  product: initialPdpProduct,
  scents,
  children,
}: React.PropsWithChildren<ProductProviderProps>) => {
  const startingVariant = productSelect.defaultVariant(initialPdpProduct);

  const startingSubscriptionPreference: PdpState["subscription"] = {
    preference: undefined,
    sellingPlan: variantSelect.orderableSubscription(startingVariant)
      ? productSelect.defaultSellingPlan(initialPdpProduct)
      : undefined,
  };

  const [store, _] = useState(() =>
    create<PdpState>()((set, get) => ({
      product: initialPdpProduct,
      focusedProduct: initialPdpProduct,
      scents,
      variant: startingVariant,
      subscription: startingSubscriptionPreference,
      actions: {
        focusProduct: (product) => set({ focusedProduct: product }),
        unfocusProduct: () => set({ focusedProduct: get().product }),
        selectProduct: (product: DroppsProduct) => {
          const currentVariantSize = get().variant.size.value;

          // Find the equivalent variant on the new product
          const newVariant = product.variants.find(
            (x) => x.size.value == currentVariantSize
          );

          // Fallback to the new product's default variant if not found
          const variant = newVariant ?? productSelect.defaultVariant(product);

          // Map the metafields for the selected variant
          const mappedVariant = {
            ...variant,
            metafields: product?.variantMetadata?.get(variant.id),
          };

          // Update the subscription information
          const { preference, sellingPlan: plan } = get().subscription;
          let newSellingPlan: PdpState["subscription"]["sellingPlan"] =
            productSelect.defaultSellingPlan(product);

          if (
            variantSelect.orderableSubscription(mappedVariant) === undefined
          ) {
            newSellingPlan = undefined;
          } else if (plan) {
            const equivPlan = productSelect.findSellingPlan(product, plan);
            newSellingPlan = equivPlan ?? newSellingPlan;
          }

          set({
            product,
            focusedProduct: product,
            subscription: {
              preference,
              sellingPlan: newSellingPlan,
            },
            variant: mappedVariant,
          });
        },
        selectVariant: (variant: DroppsVariant) => {
          const product = get().product;

          // Map the metafields for the selected variant
          const metafields = product?.variantMetadata?.get(variant.id);
          if (metafields) {
            Object.assign(variant, metafields);
          }

          set({
            variant: variant,
            subscription: {
              ...get().subscription,
              sellingPlan: variantSelect.orderableSubscription(variant)
                ? get().subscription.sellingPlan ??
                  productSelect.defaultSellingPlan(product)
                : undefined,
            },
          });
        },
        setSubscriptionPreference: (prefersSubscription: boolean) =>
          set({
            subscription: {
              ...get().subscription,
              preference: prefersSubscription,
            },
          }),
      },
    }))
  );

  useEffect(() => {
    const queryVariant = +(getQueryParam("variant") ?? "0");
    const variant = initialPdpProduct.variants.find(
      (variant) =>
        queryVariant == variant.id ||
        queryVariant == variantSelect.subscriptionVariantId(variant)
    );

    if (variant) {
      const isSubscription = variant.id !== queryVariant;
      const actions = store.getState().actions;
      actions.selectVariant(variant);
      actions.setSubscriptionPreference(isSubscription);
    }
  }, [initialPdpProduct, store]);

  return <PdpContext.Provider value={store}>{children}</PdpContext.Provider>;
};

const usePdpContext = () =>
  check(React.useContext(PdpContext), "missing pdp context");
function usePdpStore<T>(
  selector: (state: PdpState) => T,
  equalityFn?: (left: T, right: T) => boolean
): T {
  return useStore(usePdpContext(), selector, equalityFn);
}

const mapVariantMetafields = (product: DroppsProduct) => {
  // loo through the variants and add the data from the map
  product.variants.forEach((variant) => {
    const metafields = product?.variantMetadata?.get(variant.id);
    if (metafields) {
      // Spread the metafields properties directly into the variant
      Object.assign(variant, metafields);
    }
  });

  return product;
};

export const usePdpVariant = () => {
  const variant = usePdpStore((x) => x.variant);
  const product = usePdpStore((x) => x.product);
  // Map the metafields for the variant
  const metafields = product?.variantMetadata?.get(variant.id);
  if (metafields) {
    // Spread the metafields properties directly into the variant
    Object.assign(variant, metafields);
  }

  return {
    ...variant,
  };
};
export const usePdpProduct = () =>
  usePdpStore((x) => mapVariantMetafields(x.product));
export const usePdpFocusedProduct = () => usePdpStore((x) => x.focusedProduct);
export const usePdpActions = () => usePdpStore((x) => x.actions);
export const usePdpScents = () => usePdpStore((x) => x.scents);

/**
 * Returns the selected pdp selling plan. Will return falsey if the user
 * indicated a preference for one-time or if the product/variant does not have a
 * selling plan available
 */
export const usePdpSellingPlan = () =>
  usePdpStore(
    (x) => x.subscription.preference !== false && x.subscription.sellingPlan
  );
export const usePdpFocusedScent = () => {
  const product = usePdpFocusedProduct();
  const scents = usePdpScents();
  return scents.find((x) => x.name == productSelect.scent(product));
};
